import type { PropertyPrior, ChaseRecommendation } from '@/api/client'
import { buildChaseSteps, type Step, type StepState } from '@/components/review-chase/chase-steps'

const NODE_CLASSES: Record<StepState, string> = {
  do: 'border-mint bg-mint text-bg',
  skip: 'border-borderStrong bg-surface2 text-textFaint',
  na: 'border-dashed border-borderStrong bg-transparent text-textFaint',
}

// The rail is drawn in mint down to the last step actually taken and in the
// faint border colour after it, so the shape of the chase - how far along the
// four moments this reservation goes - reads before any of the words do.
const CONNECTOR_CLASSES: Record<StepState, string> = {
  do: 'bg-mint/50',
  skip: 'bg-white/12',
  na: 'bg-white/12',
}

const ACTION_CLASSES: Record<StepState, string> = {
  do: 'text-text',
  skip: 'text-textFaint',
  na: 'text-textFaint',
}

function StepNode({ index, state }: { index: number; state: StepState }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[10px] font-bold ${NODE_CLASSES[state]}`}
    >
      {state === 'do' ? index : '·'}
    </span>
  )
}

function StepRow({ step, index, isLast }: { step: Step; index: number; isLast: boolean }) {
  return (
    <li className="flex gap-3">
      {/* The rail: node plus the connector down to the next step. Purely a
          picture of the sequence - the ordered list underneath carries the
          order for anything not looking at it. */}
      <div className="flex flex-none flex-col items-center">
        <StepNode index={index} state={step.state} />
        {!isLast && (
          <span
            aria-hidden="true"
            className={`mt-1 w-0.5 flex-1 rounded-full ${CONNECTOR_CLASSES[step.state]}`}
          />
        )}
      </div>
      <div className={`min-w-0 flex-1 ${isLast ? 'pb-0' : 'pb-3'}`}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-xs font-semibold text-text">{step.name}</span>
          <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-textMuted">
            {step.when}
          </span>
        </div>
        <p className={`mt-0.5 text-xs leading-relaxed ${ACTION_CLASSES[step.state]}`}>
          {step.action}
        </p>
      </div>
    </li>
  )
}

interface ChaseDecisionProps {
  recommendation: ChaseRecommendation
  propertyPrior?: PropertyPrior | null
}

/**
 * The decision under a confidence score, drawn as the four moments in the
 * order they happen: leave the review, send the thank-you, remind at D+7,
 * and the D+13 reminder that never converts. Rendered the same way in the
 * Review Chase row panel and the reservation-detail card, so the operator
 * reads one framework everywhere. Rules live in admin-api's
 * review-confidence-recommendation.ts and docs/five-star-confidence-chase-
 * study.md - this component only displays them.
 */
export function ChaseDecision({ recommendation, propertyPrior }: ChaseDecisionProps) {
  const steps = buildChaseSteps(recommendation)

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <span className="text-xs font-medium text-textMuted">What to do</span>
      <ol className="mt-1">
        {steps.map((step, i) => (
          <StepRow key={step.key} step={step} index={i + 1} isLast={i === steps.length - 1} />
        ))}
      </ol>
      {recommendation.why.length > 0 && (
        <ul className="list-disc space-y-1 border-t border-border pl-4 pt-2 text-xs text-textMuted">
          {recommendation.why.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
      {propertyPrior && propertyPrior.stays > 0 && (
        <p className="text-xs text-textMuted">
          <span className="font-semibold text-text">{propertyPrior.name}</span>:{' '}
          {propertyPrior.five_rate}% of {propertyPrior.stays} past stays earned five stars;{' '}
          <span className={propertyPrior.sub_rate >= 10 ? 'font-semibold text-danger' : ''}>
            {propertyPrior.sub_rate}% of reviews came in below five
          </span>
          .
        </p>
      )}
    </div>
  )
}
